import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, Brain, FileText, MessageSquare, Calendar, Save, Loader2, TrendingUp } from 'lucide-react';
import { api, formatDate, DOMAIN_NAMES, getScoreLevel, BELIEF_ARCHITECTURE_PATTERNS } from '../../lib/api';
import { toast } from 'sonner';
import MindWheel from '../../components/visualizations/MindWheel';

export default function ClientView() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [reports, setReports] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Note form state
  const [noteText, setNoteText] = useState('');
  const [interpretation, setInterpretation] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reportsRes, notesRes] = await Promise.all([
          api.get(`/counselor/client/${clientId}/reports`),
          api.get(`/counselor/notes/${clientId}`)
        ]);
        setClient(reportsRes.data.client);
        setReports(reportsRes.data.reports || []);
        setNotes(notesRes.data.notes || []);
      } catch (error) {
        console.error('Error fetching client data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId]);

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      toast.error('Please enter session notes');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams({
        client_id: clientId,
        notes: noteText
      });
      if (interpretation) params.append('interpretation', interpretation);
      if (followUpDate) params.append('follow_up_date', followUpDate);

      await api.post(`/counselor/notes?${params.toString()}`);
      toast.success('Note saved successfully');
      
      // Refresh notes
      const notesRes = await api.get(`/counselor/notes/${clientId}`);
      setNotes(notesRes.data.notes || []);
      
      // Clear form
      setNoteText('');
      setInterpretation('');
      setFollowUpDate('');
    } catch (error) {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading client data...</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Client not found</p>
        <Link to="/counselor">
          <Button variant="outline" className="mt-4">Back to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const latestReport = reports[0];
  const mindIndex = latestReport?.mind_index || 0;
  const level = getScoreLevel(100 - mindIndex);
  const pattern = latestReport?.belief_architecture_pattern || latestReport?.cluster_profile;
  const cluster = BELIEF_ARCHITECTURE_PATTERNS[pattern?.primary_cluster];

  return (
    <div className="space-y-6" data-testid="client-view-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/counselor">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{client.name}</h1>
            <p className="text-muted-foreground">{client.email}</p>
          </div>
        </div>
        {latestReport && (
          <div className="text-right">
            <p className="text-xs font-mono text-muted-foreground">Mind Index</p>
            <p className="text-3xl font-bold font-mono" style={{ color: level.color }}>
              {mindIndex.toFixed(1)}
            </p>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {latestReport && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div 
                  className="h-10 w-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${cluster?.color || '#6366F1'}20` }}
                >
                  <Brain className="h-5 w-5" style={{ color: cluster?.color || '#6366F1' }} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pattern Cluster</p>
                  <p className="font-medium">{cluster?.name || 'Unknown'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-amber-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Top Bottleneck</p>
                  <p className="font-medium">{latestReport.top_bottlenecks?.[0] || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center">
                  <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Assessments</p>
                  <p className="font-medium">{reports.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="reports" className="space-y-6">
        <TabsList>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="notes">Session Notes</TabsTrigger>
          <TabsTrigger value="add-note">Add Note</TabsTrigger>
        </TabsList>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          {latestReport ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Mind Wheel */}
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Mind Architecture</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MindWheel domainScores={latestReport.domain_scores} />
                  </CardContent>
                </Card>

                {/* Executive Summary */}
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Executive Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      {latestReport.executive_summary}
                    </p>
                    <div className="mt-6 space-y-4">
                      <div>
                        <p className="text-sm font-medium mb-2">Top Bottlenecks</p>
                        <div className="flex flex-wrap gap-2">
                          {latestReport.top_bottlenecks?.map((b, i) => (
                            <Badge key={i} variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                              {b}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium mb-2">Strengths</p>
                        <div className="flex flex-wrap gap-2">
                          {latestReport.top_strengths?.map((s, i) => (
                            <Badge key={i} variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Root Cause */}
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold">Root Cause Hypothesis</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {latestReport.root_cause_hypothesis}
                  </p>
                </CardContent>
              </Card>

              {/* All Reports */}
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold">Assessment History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {reports.map((report) => (
                      <div 
                        key={report.report_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/30"
                      >
                        <div>
                          <p className="font-medium text-sm">{formatDate(report.created_at)}</p>
                          <p className="text-xs text-muted-foreground">
                            {BELIEF_ARCHITECTURE_PATTERNS[report.belief_architecture_pattern?.primary_cluster || report.cluster_profile?.primary_cluster]?.name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold" style={{ color: getScoreLevel(100 - report.mind_index).color }}>
                            {report.mind_index.toFixed(1)}
                          </p>
                          <p className="text-xs text-muted-foreground">Mind Index</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-card border-border/50">
              <CardContent className="py-12 text-center text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" strokeWidth={1} />
                <p>No reports available for this client</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          {notes.length === 0 ? (
            <Card className="bg-card border-border/50">
              <CardContent className="py-12 text-center text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" strokeWidth={1} />
                <p>No session notes yet</p>
              </CardContent>
            </Card>
          ) : (
            notes.map((note) => (
              <motion.div
                key={note.note_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium">
                        {formatDate(note.session_date)}
                      </CardTitle>
                      {note.follow_up_date && (
                        <Badge variant="outline">
                          <Calendar className="h-3 w-3 mr-1" />
                          Follow-up: {formatDate(note.follow_up_date)}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-xs font-mono text-muted-foreground mb-1">Session Notes</p>
                      <p className="text-muted-foreground">{note.notes}</p>
                    </div>
                    {note.interpretation && (
                      <div>
                        <p className="text-xs font-mono text-muted-foreground mb-1">Interpretation</p>
                        <p className="text-muted-foreground">{note.interpretation}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </TabsContent>

        {/* Add Note Tab */}
        <TabsContent value="add-note">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">New Session Note</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="notes">Session Notes *</Label>
                <Textarea
                  id="notes"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Document the session findings, client state, and discussion points..."
                  rows={5}
                  data-testid="session-notes-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="interpretation">Interpretation (Optional)</Label>
                <Textarea
                  id="interpretation"
                  value={interpretation}
                  onChange={(e) => setInterpretation(e.target.value)}
                  placeholder="Your professional interpretation of the client's mental architecture..."
                  rows={3}
                  data-testid="interpretation-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="follow-up">Follow-up Date (Optional)</Label>
                <Input
                  id="follow-up"
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  data-testid="follow-up-date-input"
                />
              </div>

              <Button 
                onClick={handleSaveNote}
                disabled={saving}
                className="w-full"
                data-testid="save-note-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Session Note
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
